import React from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { colour } from '@leagueplatform/ui-kit';
import {
  useSalesforceMessagingState,
  SALESFORCE_LIVE_CHAT_FEATURE_FLAG,
} from '@leagueplatform/salesforce-live-chat';
import {
  getChatAvailability,
  GET_CHAT_AVAILABILITY_MESSAGE_TYPE,
} from '@leagueplatform/salesforce-live-chat-api';
import { useFeatureFlagQuery } from '@leagueplatform/user-profile-api';
import { useIntl } from '@leagueplatform/locales';

const CUSTOMER_CARE_AGENT_TYPE = 'customer_care';
const CHAT_FAB_ID = 'chat-fab';

const SkipLink = styled.a`
  position: fixed;
  top: 0;
  left: 0;
  transform: translateY(-100%);
  z-index: 9999;
  padding: 8px 16px;
  background: ${colour('white')};
  border: 3px solid ${colour('purple')};
  border-radius: 4px;
  color: ${colour('purple')};
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;

  &:focus {
    transform: translateY(0);
    outline: none;
  }
`;

const SalesforceChatSkipLink = () => {
  const { formatMessage } = useIntl();
  const { data: sfEnabled } = useFeatureFlagQuery(
    SALESFORCE_LIVE_CHAT_FEATURE_FLAG,
  );
  const { isReady, windowState } = useSalesforceMessagingState(sfEnabled);

  const { data } = useQuery(
    [GET_CHAT_AVAILABILITY_MESSAGE_TYPE],
    getChatAvailability,
    {
      enabled: sfEnabled && isReady,
      refetchOnWindowFocus: true,
      staleTime: 60 * 1000,
    },
  );

  if (!sfEnabled || !isReady) return null;

  const isMaximized = windowState === 'maximized';
  const isAgentAvailable =
    data?.availabilities?.find(a => a.agent_type === CUSTOMER_CARE_AGENT_TYPE)
      ?.agent_is_available ?? false;

  if (!isMaximized && !isAgentAvailable) return null;

  const handleClick = e => {
    e.preventDefault();
    if (isMaximized) {
      // Salesforce renders its chat UI inside an iframe with an id prefixed
      // "embeddedMessagingFrame". We can't focus inside it (cross-origin
      // boundary), so we focus the iframe element itself to hand keyboard
      // control to the chat window.
      const iframe = document.querySelector(
        'iframe[id^="embeddedMessagingFrame"]',
      );
      if (iframe) iframe.focus();
    } else {
      document.getElementById(CHAT_FAB_ID)?.focus();
    }
  };

  return (
    <SkipLink href={`#${CHAT_FAB_ID}`} onClick={handleClick}>
      {formatMessage({
        id: isMaximized ? 'SKIP_TO_CHAT_WINDOW' : 'SKIP_TO_CHAT',
      })}
    </SkipLink>
  );
};

export default SalesforceChatSkipLink;
